// The drawer's read (day-view.md §7): one seans in full from
// GET /api/session/:id, fetched when a cell opens and again after
// every action on it. The board already drew the cell, so the drawer
// shows that at once and fills its panels when this arrives; a
// refetch keeps the old panels on screen until the new ones land.

import {useEffect, useState} from "react";
import {getSession} from "../api/client";
import type {SessionDetail} from "../api/contract";
import {errorText} from "./useActions";

export interface DetailKey {
  providerId: string;
  eventId: string;
  date: string;
}

interface DetailState {
  fetchKey: string; // which (seans, version) this answer is for
  sessionKey: string; // which seans — a refetch keeps the old answer
  detail: SessionDetail | null;
  error: string | null;
}

const NONE: DetailState = {fetchKey: "", sessionKey: "", detail: null, error: null};

export function useSessionDetail(
  key: DetailKey | null, version: number,
  // Dev only: the fixture's answer instead of the door.
  demo?: (key: DetailKey) => SessionDetail | null,
) {
  const providerId = key?.providerId ?? "";
  const eventId = key?.eventId ?? "";
  const date = key?.date ?? "";
  const sessionKey = key ? `${providerId}|${eventId}|${date}` : "";
  const fetchKey = sessionKey ? `${sessionKey}#${version}` : "";
  const [state, setState] = useState<DetailState>(NONE);

  useEffect(() => {
    if (!fetchKey || demo) return;
    const ctl = new AbortController();
    getSession(providerId, eventId, date, ctl.signal).then(({data}) => {
      if (!ctl.signal.aborted) setState({fetchKey, sessionKey, detail: data, error: null});
    }).catch((e: unknown) => {
      if (ctl.signal.aborted) return;
      setState((s) => ({
        fetchKey, sessionKey, error: errorText(e),
        detail: s.sessionKey === sessionKey ? s.detail : null,
      }));
    });
    return () => ctl.abort();
  }, [fetchKey, sessionKey, providerId, eventId, date, demo]);

  if (demo) {
    const detail = key ? demo(key) : null;
    return {detail, error: detail || !key ? null : "DEMO — bu hücre için kurgu detay yok",
      loading: false};
  }
  const same = state.sessionKey === sessionKey;
  return {
    detail: same ? state.detail : null,
    error: state.fetchKey === fetchKey ? state.error : null,
    loading: !!fetchKey && state.fetchKey !== fetchKey,
  };
}
